import { Link } from "react-router-dom";
import { Wallet, ExternalLink } from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { formatDate, formatMXN } from "@/lib/utils";
import { useFacturasCliente, useEstadoCuenta } from "./api";
import { ESTATUS_LABEL, ESTATUS_VARIANT } from "./helpers";
// rc139 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

type Props = {
  clienteId: string;
  /** Label del cliente (razón social) para el breadcrumb back en FacturaDetailPage. */
  fromLabel?: string;
};

export function ClienteFacturasTab({ clienteId, fromLabel }: Props) {
  const { data, isLoading } = useFacturasCliente(clienteId);
  const { data: estado } = useEstadoCuenta(clienteId);
  const items = data?.items ?? [];
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      {/* Strip de estado de cuenta */}
      {estado && (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <StatCard label="Total facturado" value={formatMXN(estado.total_facturado)} />
          <StatCard label="Total pagado" value={formatMXN(estado.total_pagado)} />
          <StatCard
            label="Saldo pendiente"
            value={formatMXN(estado.saldo_pendiente)}
            variant={Number(estado.saldo_pendiente) > 0 ? "destructive" : "success"}
          />
          <StatCard
            label="Facturas vencidas"
            value={String(estado.facturas_vencidas)}
            variant={estado.facturas_vencidas > 0 ? "destructive" : undefined}
          />
        </div>
      )}

      {/* rc139 — móvil: tarjetas */}
      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>Este cliente no tiene facturas emitidas todavía.</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((f) => (
              <MobileCard
                key={f.id}
                to={`/facturas/${f.id}`}
                state={fromLabel ? { from: `/clientes/${clienteId}`, fromLabel } : undefined}
                title={<span className="font-mono">{f.folio}</span>}
                subtitle={f.tipo === "libre" ? "Servicio adicional" : undefined}
                right={<Badge variant={ESTATUS_VARIANT[f.estatus]}>{ESTATUS_LABEL[f.estatus]}</Badge>}
              >
                <MobileMeta label="Total" mono>{formatMXN(f.total)}</MobileMeta>
                <MobileMeta label="Saldo" mono>
                  {Number(f.saldo) > 0 ? <span className="text-destructive">{formatMXN(f.saldo)}</span> : <span className="text-muted-foreground">{formatMXN(0)}</span>}
                </MobileMeta>
                <MobileMeta label="Emisión">{formatDate(f.fecha_emision)}</MobileMeta>
                <MobileMeta label="Vence">{formatDate(f.fecha_vencimiento)}</MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio</TableHead>
              <TableHead>Emisión</TableHead>
              <TableHead>Vencimiento</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Wallet}
                  title="Sin facturas"
                  description="Este cliente no tiene facturas emitidas todavía."
                />
              </TableEmpty>
            ) : (
              items.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="font-mono text-xs">
                    <div className="flex items-center gap-1.5">
                      {f.folio}
                      {f.tipo === "libre" && (
                        <Badge variant="outline" className="border-blue-300 bg-blue-50 text-[10px] text-blue-800">
                          Servicio adicional
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{formatDate(f.fecha_emision)}</TableCell>
                  <TableCell className="text-xs">
                    {formatDate(f.fecha_vencimiento)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMXN(f.total)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {Number(f.saldo) > 0 ? (
                      <span className="text-destructive">{formatMXN(f.saldo)}</span>
                    ) : (
                      <span className="text-muted-foreground">{formatMXN(0)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={ESTATUS_VARIANT[f.estatus]}>
                      {ESTATUS_LABEL[f.estatus]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/facturas/${f.id}`}
                      state={
                        fromLabel
                          ? { from: `/clientes/${clienteId}`, fromLabel }
                          : undefined
                      }
                      className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                    >
                      Ver
                      <ExternalLink size={10} />
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      )}
    </div>
  );
}

function StatCard({
  label, value, variant,
}: {
  label: string;
  value: string;
  variant?: "destructive" | "success";
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border bg-card p-3">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={[
          "text-base leading-none font-semibold",
          variant === "destructive" ? "text-destructive" : "",
          variant === "success" ? "text-emerald-600 dark:text-emerald-400" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {value}
      </span>
    </div>
  );
}
